import {
  takeEvery,
  call,
  put,
} from 'redux-saga/effects';
import { ModalsActionCreators, PhotosActionCreators } from '../actions/creators';
import { generateErrorMessage } from '../helpers';
import { PhotosActionTypes } from '../actions/types';
import { AsyncActionStatus } from '../enums';
import API from '../../api';
import { toast } from '../../utilities';

function* fetchPhotosAndResources(action) {
  try {
    const response = yield call(
      API.Photos.getPhotosAndResources,
      action.payload.userId,
      action.payload.projectId,
    );
    yield put(PhotosActionCreators.fetchPhotosAndResources[AsyncActionStatus.FULFILLED](
      response.data.photos,
      response.data.project,
      response.data.chapters,
      response.data.book,
    ));
  } catch (error) {
    yield put(PhotosActionCreators.fetchPhotosAndResources[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* addPhotoByProjectId(action) {
  try {
    const response = yield call(
      API.Photos.addPhotoByProjectId,
      action.payload.projectId,
      action.payload.photo,
    );
    yield put(PhotosActionCreators.addPhotoByProjectId[AsyncActionStatus.FULFILLED](
      response.data.photos,
      response.data.book,
    ));
    if (response.data.isPhotosLimitExceeded) yield call(toast.error, t('toastMessages.error.photosLimitExceeded', { ns: i18next.Namespace.COMMON }));
    else yield call(toast.success, t('toastMessages.success.photoAdded', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(PhotosActionCreators.addPhotoByProjectId[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* addMultiplePhotos(action) {
  try {
    const response = yield call(
      API.Photos.addMultiplePhotos,
      action.payload.projectId,
      action.payload.photos,
    );
    yield put(PhotosActionCreators.addMultiplePhotos[AsyncActionStatus.FULFILLED](
      response.data.photos,
      response.data.book,
    ));
    if (response.data.isPhotosLimitExceeded) yield call(toast.error, t('toastMessages.error.photosLimitExceeded', { ns: i18next.Namespace.COMMON }));
    else yield call(toast.success, t('toastMessages.success.photosAdded', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(PhotosActionCreators.addMultiplePhotos[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* updateMultiplePhotos(action) {
  try {
    const response = yield call(
      API.Photos.updateMultiplePhotos,
      action.payload.photos,
    );
    yield put(PhotosActionCreators.updateMultiplePhotos[AsyncActionStatus.FULFILLED](
      response.data.photos,
      response.data.book,
    ));
    if (!action.payload.silentNotificationMode) {
      yield call(toast.success, t('toastMessages.success.photoUpdated', { ns: i18next.Namespace.COMMON }));
    }
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(PhotosActionCreators.updateMultiplePhotos[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* updatePhoto(action) {
  try {
    const response = yield call(
      API.Photos.updatePhoto,
      action.payload.photoId,
      action.payload.photo,
      action.payload.objectPositionAndZoom,
    );
    yield put(PhotosActionCreators.updatePhoto[AsyncActionStatus.FULFILLED](
      response.data.photo,
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.photoUpdated', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(PhotosActionCreators.updatePhoto[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* deletePhotoWithIndexShifting(action) {
  try {
    const response = yield call(
      API.Photos.deletePhotoWithIndexShifting,
      action.payload.photoId,
    );
    yield put(PhotosActionCreators.deletePhotoWithIndexShifting[AsyncActionStatus.FULFILLED](
      response.data.removedPhoto,
      response.data.chapterPhotos,
    ));
    yield call(toast.success, t('toastMessages.success.photoRemoved', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(PhotosActionCreators.deletePhotoWithIndexShifting[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* sendPhotosUploadInfo(action) {
  try {
    const response = yield call(
      API.Photos.sendPhotosUploadInfo,
      action.payload.projectId,
      action.payload.photosCount,
      action.payload.captionsCount,
    );
    yield put(PhotosActionCreators.sendPhotosUploadInfo[AsyncActionStatus.FULFILLED](response.data.book));
    yield call(toast.success, t('toastMessages.success.editorEmailSent', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(PhotosActionCreators.sendPhotosUploadInfo[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* replaceCoverPhoto(action) {
  try {
    const response = yield call(API.Photos.replaceCoverPhoto, action.payload.photo);
    yield put(PhotosActionCreators.replaceCoverPhoto[AsyncActionStatus.FULFILLED](
      response.data.photos,
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.coverUploaded', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(PhotosActionCreators.replaceCoverPhoto[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* chooseCoverPhoto(action) {
  try {
    const response = yield call(API.Photos.chooseCoverPhoto, action.payload.projectId);
    yield put(PhotosActionCreators.chooseCoverPhoto[AsyncActionStatus.FULFILLED](
      response.data.photos,
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.coverUpdated', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(PhotosActionCreators.chooseCoverPhoto[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

function* getCoverPhotosByProjectId(action) {
  try {
    const response = yield call(
      API.Photos.getCoverPhotosByProjectId,
      action.payload.projectId,
    );
    yield put(PhotosActionCreators.getCoverPhotosByProjectId[AsyncActionStatus.FULFILLED](response.data.photos));
  } catch (error) {
    yield put(PhotosActionCreators.getCoverPhotosByProjectId[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* getCoverPhotos() {
  try {
    const response = yield call(API.Photos.getCoverPhotos);
    yield put(PhotosActionCreators.getCoverPhotos[AsyncActionStatus.FULFILLED](response.data.photos));
  } catch (error) {
    yield put(PhotosActionCreators.getCoverPhotos[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* deleteCoverPhotos(action) {
  try {
    const response = yield call(
      API.Photos.deleteCoverPhotos,
      action.payload.photoIds,
    );

    yield put(PhotosActionCreators.deleteCoverPhotos[AsyncActionStatus.FULFILLED](
      response.data.updatedPhoto,
      response.data.book,
    ));
    yield call(toast.success, t('toastMessages.success.photoRemoved', { ns: i18next.Namespace.COMMON }));
    yield put(ModalsActionCreators.closeDeleteCoverPhotosModal());
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(PhotosActionCreators.deleteCoverPhotos[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* addCoverPhoto(action) {
  try {
    const response = yield call(
      API.Photos.addCoverPhoto,
      action.payload.projectId,
      action.payload.photo,
    );
    yield put(PhotosActionCreators.addCoverPhoto[AsyncActionStatus.FULFILLED](
      response.data.photos,
      response.data.book,
    ));
    if (!response.data.isPhotosLimitExceeded) yield call(toast.success, t('toastMessages.success.photoAdded', { ns: i18next.Namespace.COMMON }));
    else yield call(toast.error, t('toastMessages.error.coverPhotosLimitExceeded', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(PhotosActionCreators.addCoverPhoto[AsyncActionStatus.REJECTED](
      message,
    ));
    yield call(toast.error, message);
  }
}

export default [
  takeEvery(
    PhotosActionTypes.fetchPhotosAndResources[AsyncActionStatus.PENDING],
    fetchPhotosAndResources,
  ),
  takeEvery(
    PhotosActionTypes.addPhotoByProjectId[AsyncActionStatus.PENDING],
    addPhotoByProjectId,
  ),
  takeEvery(
    PhotosActionTypes.addMultiplePhotos[AsyncActionStatus.PENDING],
    addMultiplePhotos,
  ),
  takeEvery(
    PhotosActionTypes.updateMultiplePhotos[AsyncActionStatus.PENDING],
    updateMultiplePhotos,
  ),
  takeEvery(
    PhotosActionTypes.updatePhoto[AsyncActionStatus.PENDING],
    updatePhoto,
  ),
  takeEvery(
    PhotosActionTypes.deletePhotoWithIndexShifting[AsyncActionStatus.PENDING],
    deletePhotoWithIndexShifting,
  ),
  takeEvery(
    PhotosActionTypes.sendPhotosUploadInfo[AsyncActionStatus.PENDING],
    sendPhotosUploadInfo,
  ),
  takeEvery(
    PhotosActionTypes.replaceCoverPhoto[AsyncActionStatus.PENDING],
    replaceCoverPhoto,
  ),
  takeEvery(
    PhotosActionTypes.chooseCoverPhoto[AsyncActionStatus.PENDING],
    chooseCoverPhoto,
  ),
  takeEvery(
    PhotosActionTypes.getCoverPhotosByProjectId[AsyncActionStatus.PENDING],
    getCoverPhotosByProjectId,
  ),
  takeEvery(
    PhotosActionTypes.getCoverPhotos[AsyncActionStatus.PENDING],
    getCoverPhotos,
  ),
  takeEvery(
    PhotosActionTypes.deleteCoverPhotos[AsyncActionStatus.PENDING],
    deleteCoverPhotos,
  ),
  takeEvery(
    PhotosActionTypes.addCoverPhoto[AsyncActionStatus.PENDING],
    addCoverPhoto,
  ),
];
